import { Checkbox, ErrorMessage, HintIcon, Space, Text } from '@/components';
import type { CheckboxProps } from 'antd';
import type { CSSProperties } from 'react';
import {
  Controller,
  type FieldErrors,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { Form } from '.';
import { FieldLabel, type FieldLabelSize } from './FieldLabel';
import type { FormControlProp } from './types';

type Props<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  text: string;
  hint?: string;
  control: FormControlProp<T>;
  errors: FieldErrors;
  required?: boolean;
  disabled?: boolean;
  shouldCoexistOnChange?: boolean;
  wrapperStyle?: CSSProperties;
  labelSize?: FieldLabelSize;
} & Pick<CheckboxProps, 'onChange'>;

export const CheckboxField = <T extends FieldValues>({
  name,
  label,
  text,
  hint,
  control,
  errors,
  required,
  disabled,
  onChange,
  shouldCoexistOnChange = false,
  wrapperStyle,
  labelSize = 'medium',
}: Props<T>) => {
  return (
    <Form.Item
      label={
        label && (
          <FieldLabel
            text={label}
            required={required}
            hint={hint}
            size={labelSize}
          />
        )
      }
      style={wrapperStyle}
    >
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange: fieldOnChange, ...field } }) => (
          <Checkbox
            {...field}
            checked={field.value}
            disabled={disabled}
            onChange={(e) => {
              if (shouldCoexistOnChange || !onChange) {
                fieldOnChange(e.target.checked);
              }
              onChange?.(e);
            }}
            data-dd-privacy="allow"
          >
            <Space>
              <Text weight="regular" text={text} />
              {hint && !label && <HintIcon hint={hint} />}
            </Space>
          </Checkbox>
        )}
      />
      <ErrorMessage errors={errors} name={name} />
    </Form.Item>
  );
};
